import { supabase } from './supabase';
import type { NylasEmail, NylasSendMessagePayload, NylasFolder } from '../types/nylas';
import { getCached, setCache, invalidateCache, isRateLimited, setRateLimitCooldown, resetConsecutiveRateLimits, CACHE_TTL, isNylasDisabled, checkAndIncrementBudget } from './nylasCache';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const FETCH_TIMEOUT_MS = 30000;

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

async function callNylasEmail(action: string, params?: Record<string, string>, body?: any): Promise<any> {
  if (isNylasDisabled()) {
    throw new Error('nylas_disabled: Email sync is paused for scheduled maintenance. All other features remain available.');
  }

  if (action !== 'send' && !checkAndIncrementBudget()) {
    throw new Error('budget_exceeded: Hourly request limit reached. Please try again later.');
  }

  if (isRateLimited() && action !== 'send') {
    throw new Error('provider_busy: Microsoft is temporarily limiting access. Please wait a moment and try again.');
  }

  const headers = await getAuthHeaders();
  const url = new URL(`${SUPABASE_URL}/functions/v1/nylas-email`);
  url.searchParams.set('action', action);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  const options: RequestInit = { headers, signal: controller.signal };
  if (body) {
    options.method = 'POST';
    options.body = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(url.toString(), options);
  } catch (err: any) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      setRateLimitCooldown(45000);
      throw new Error('provider_timeout: Request timed out. The email provider may be temporarily unreachable. Please try again.');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    const errorMsg = errorData.error || `Request failed: ${response.status}`;
    if (errorMsg.includes('grant_invalid') || errorMsg.includes('re-authenticate') || errorMsg.includes('expired')) {
      throw new Error('grant_invalid: Your Outlook connection has expired. Please reconnect your account.');
    }
    if (errorMsg.includes('rate_limit') || response.status === 429) {
      const retryAfterHeader = response.headers.get('Retry-After');
      const retryAfterBody = errorData.retry_after;
      const retryAfterSeconds = parseInt(retryAfterHeader || retryAfterBody || '0', 10);
      const cooldownMs = retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : undefined;
      setRateLimitCooldown(cooldownMs);
      throw new Error('provider_busy: Microsoft is temporarily busy. Please wait a moment and try again.');
    }
    if (errorMsg.includes('provider_timeout') || response.status === 504) {
      setRateLimitCooldown(30000);
      throw new Error('provider_timeout: The email provider is temporarily unresponsive. Please try again shortly.');
    }
    throw new Error(errorMsg);
  }

  resetConsecutiveRateLimits();
  return response.json();
}

export type EmailFolder = 'inbox' | 'sent' | 'drafts' | 'trash' | 'starred' | string;

export async function checkGrantStatus(): Promise<{ valid: boolean; status: string; error?: string }> {
  const cacheKey = 'grant-status';
  const cached = getCached<{ valid: boolean; status: string; error?: string }>(cacheKey);
  if (cached) return cached;

  try {
    const result = await callNylasEmail('status');
    const status = { valid: result.grant_status !== 'invalid', status: result.grant_status || 'unknown' };
    setCache(cacheKey, status, CACHE_TTL.GRANT_STATUS);
    return status;
  } catch (err: any) {
    if (err.message?.includes('grant_invalid')) {
      const status = { valid: false, status: 'invalid', error: err.message };
      setCache(cacheKey, status, CACHE_TTL.GRANT_STATUS);
      return status;
    }
    return { valid: true, status: 'unknown', error: err.message };
  }
}

export async function listEmails(folder: EmailFolder = 'inbox', limit = 25): Promise<NylasEmail[]> {
  const cacheKey = `emails-${folder}-${limit}`;
  const cached = getCached<NylasEmail[]>(cacheKey);
  if (cached) return cached;

  const result = await callNylasEmail('list', { folder, limit: String(limit) });
  const emails = result.data || [];
  setCache(cacheKey, emails, CACHE_TTL.EMAIL_LIST);
  return emails;
}

export async function listFolders(): Promise<NylasFolder[]> {
  const cacheKey = 'nylas-folders';
  const cached = getCached<NylasFolder[]>(cacheKey);
  if (cached) return cached;

  const result = await callNylasEmail('folders');
  const folders = result.data || [];
  setCache(cacheKey, folders, 300000);
  return folders;
}

export async function createFolder(name: string): Promise<NylasFolder> {
  const result = await callNylasEmail('create-folder', undefined, { name });
  invalidateCache('nylas-folders');
  return result.data || result;
}

export async function moveMessageToFolder(messageId: string, folderId: string): Promise<boolean> {
  try {
    await callNylasEmail('move', undefined, { messageId, folderId });
    invalidateCache('emails-');
    return true;
  } catch (err) {
    console.error('moveMessageToFolder error:', err);
    return false;
  }
}

export async function moveMessagesToFolder(messageIds: string[], folderId: string): Promise<boolean> {
  try {
    const results = await Promise.all(
      messageIds.map(id => callNylasEmail('move', undefined, { messageId: id, folderId }))
    );
    invalidateCache('emails-');
    return results.length === messageIds.length;
  } catch (err) {
    console.error('moveMessagesToFolder error:', err);
    return false;
  }
}

export async function getEmail(messageId: string): Promise<NylasEmail> {
  const cacheKey = `email-${messageId}`;
  const cached = getCached<NylasEmail>(cacheKey);
  if (cached) return cached;

  const result = await callNylasEmail('get', { messageId });
  const email = result.data || result;
  setCache(cacheKey, email, CACHE_TTL.SINGLE_EMAIL);
  return email;
}

export async function getThread(threadId: string): Promise<any> {
  const cacheKey = `thread-${threadId}`;
  const cached = getCached<any>(cacheKey);
  if (cached) return cached;

  const result = await callNylasEmail('thread', { threadId });
  const thread = result.data || result;
  setCache(cacheKey, thread, CACHE_TTL.SINGLE_EMAIL);
  return thread;
}

export async function sendMessage(payload: NylasSendMessagePayload): Promise<any> {
  const result = await callNylasEmail('send', undefined, payload);
  invalidateCache('emails-');
  return result.data || result;
}

export async function searchEmails(query: string, limit = 20): Promise<NylasEmail[]> {
  const cacheKey = `search-${query}-${limit}`;
  const cached = getCached<NylasEmail[]>(cacheKey);
  if (cached) return cached;

  const result = await callNylasEmail('search', { q: query, limit: String(limit) });
  const emails = result.data || [];
  setCache(cacheKey, emails, CACHE_TTL.EMAIL_SEARCH);
  return emails;
}

export function invalidateEmailCache(): void {
  invalidateCache('emails-');
  invalidateCache('search-');
}

function mapCachedRowToEmail(row: any): NylasEmail {
  return {
    id: row.nylas_message_id,
    grant_id: row.grant_id,
    thread_id: row.thread_id || undefined,
    subject: row.subject || '',
    from: row.from_participants || [],
    to: row.to_participants || [],
    cc: row.cc_participants || [],
    bcc: row.bcc_participants || [],
    body: row.body || '',
    snippet: row.snippet || undefined,
    date: row.date || 0,
    unread: row.unread ?? false,
    starred: row.starred ?? false,
    folders: row.folders || [],
    attachments: row.attachments || [],
  };
}

const SYSTEM_FOLDERS = ['inbox', 'sent', 'drafts', 'trash', 'starred'];

export async function listCachedEmails(folder: EmailFolder = 'inbox', limit = 50, folderId?: string): Promise<NylasEmail[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data: grant } = await supabase
    .from('nylas_grants')
    .select('email, grant_id')
    .eq('user_id', user.id)
    .eq('status', 'active')
    .order('connected_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  const userEmail = (grant?.email || '').toLowerCase();
  const grantId = grant?.grant_id || '';

  if (!grantId) return [];

  let query = supabase
    .from('cached_nylas_emails')
    .select('*')
    .order('date', { ascending: false });

  if (grantId) {
    query = query.eq('grant_id', grantId);
  }

  if (folder === 'trash') {
    query = query.eq('is_deleted', true);
  } else {
    query = query.eq('is_deleted', false);
  }

  if (folder === 'starred') {
    query = query.eq('starred', true);
  }

  // Use the resolved folderId to filter by the actual folder ID in the folders array
  if (folderId) {
    query = query.contains('folders', [folderId]);
  }

  // For custom (non-system) folders without a pre-resolved folderId, filter by the folder value directly
  const isCustomFolder = !SYSTEM_FOLDERS.includes(folder.toLowerCase());
  if (isCustomFolder && !folderId) {
    query = query.contains('folders', [folder]);
  }

  // Fetch more than needed for client-side folder filtering
  const fetchLimit = folder === 'sent' ? limit * 4 : limit;
  query = query.limit(fetchLimit);

  const { data, error } = await query;
  if (error) {
    console.error('Error fetching cached emails:', error);
    return [];
  }

  let rows = data || [];

  // Client-side filter for sent emails (case-insensitive email matching)
  if (folder === 'sent' && userEmail) {
    rows = rows.filter((row: any) => {
      const fromList = row.from_participants || [];
      return fromList.some((p: any) => (p.email || '').toLowerCase() === userEmail);
    });
  }

  return rows.slice(0, limit).map(mapCachedRowToEmail);
}

export async function getThreadCounts(): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from('cached_nylas_emails')
    .select('thread_id')
    .eq('is_deleted', false)
    .not('thread_id', 'is', null);

  if (error || !data) return {};

  const counts: Record<string, number> = {};
  for (const row of data) {
    if (row.thread_id) {
      counts[row.thread_id] = (counts[row.thread_id] || 0) + 1;
    }
  }
  return counts;
}

export async function updateCachedEmailField(
  messageId: string,
  fields: { unread?: boolean; starred?: boolean; is_deleted?: boolean }
): Promise<boolean> {
  const { error } = await supabase
    .from('cached_nylas_emails')
    .update(fields)
    .eq('nylas_message_id', messageId);

  return !error;
}

export async function getCachedEmailCount(): Promise<number> {
  const { count, error } = await supabase
    .from('cached_nylas_emails')
    .select('*', { count: 'exact', head: true })
    .eq('is_deleted', false);

  if (error) return 0;
  return count || 0;
}

export interface BackfillJobStatus {
  id: string;
  status: string;
  messages_fetched: number;
  pages_processed: number;
  last_rate_limit_at: string | null;
  retry_after_seconds: number | null;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export async function getBackfillJobStatus(): Promise<BackfillJobStatus[]> {
  const { data, error } = await supabase
    .from('nylas_backfill_jobs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(5);

  if (error) {
    console.error('Error fetching backfill status:', error);
    return [];
  }
  return data || [];
}

export async function triggerBackfill(action: 'start' | 'resume' = 'start', jobId?: string): Promise<any> {
  const headers = await getAuthHeaders();
  const url = new URL(`${SUPABASE_URL}/functions/v1/nylas-email-backfill`);
  url.searchParams.set('action', action);
  if (jobId) url.searchParams.set('job_id', jobId);

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers,
  });

  const result = await response.json();
  if (!response.ok && response.status !== 409) {
    throw new Error(result.error || 'Failed to trigger backfill');
  }
  return result;
}

export async function getLastWebhookTimestamp(): Promise<string | null> {
  const { data } = await supabase
    .from('nylas_webhook_events')
    .select('received_at')
    .eq('processed', true)
    .order('received_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  return data?.received_at || null;
}

export type MessageAction = 'markRead' | 'markUnread' | 'star' | 'unstar' | 'trash' | 'archive';

export async function performMessageAction(
  action: MessageAction,
  messageId: string
): Promise<boolean> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${SUPABASE_URL}/functions/v1/nylas-message-actions`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ action, messageId }),
    });
    const result = await response.json();
    return result.results?.[0]?.success ?? false;
  } catch (err) {
    console.error('performMessageAction error:', err);
    return false;
  }
}

export async function performBulkMessageAction(
  action: MessageAction,
  messageIds: string[]
): Promise<boolean> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${SUPABASE_URL}/functions/v1/nylas-message-actions`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ action, messageIds }),
    });
    const result = await response.json();
    return result.results?.every((r: any) => r.success) ?? false;
  } catch (err) {
    console.error('performBulkMessageAction error:', err);
    return false;
  }
}

export function getAttachmentDownloadUrl(messageId: string, attachmentId: string): string {
  return `${SUPABASE_URL}/functions/v1/nylas-attachment?messageId=${encodeURIComponent(messageId)}&attachmentId=${encodeURIComponent(attachmentId)}`;
}

export async function downloadAttachment(messageId: string, attachmentId: string, filename: string): Promise<void> {
  const headers = await getAuthHeaders();
  const url = getAttachmentDownloadUrl(messageId, attachmentId);
  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error('Failed to download attachment');
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

export async function createSignature(body: string, name?: string): Promise<{ signature_id: string }> {
  return callNylasEmail('signature-create', undefined, { body, name: name || 'Default Signature' });
}

export async function getSignatures(): Promise<any> {
  return callNylasEmail('signature-get');
}

export async function updateSignature(signatureId: string, body: string, name?: string): Promise<any> {
  return callNylasEmail('signature-update', undefined, { signatureId, body, name });
}

export async function deleteSignature(signatureId: string): Promise<any> {
  return callNylasEmail('signature-delete', { signatureId });
}


